/**
 * The backtest engine.
 *
 * ## The one decision this file is built around
 *
 * **A backtest produces fills, and the fills go through the journal.** P&L,
 * R-multiple, win rate, expectancy and drawdown all come from
 * `reconstructTrades` and `summarise` — the same functions that score the
 * trader's real broker fills. Nothing here computes money.
 *
 * That is what makes the feature worth having. The point of a backtest inside a
 * journal is to put the hypothetical record beside the real one; if the two
 * were scored by different arithmetic the comparison would be meaningless, and
 * the difference would be invisible until somebody chased a hundred dollars
 * that was never there.
 *
 * ## What a bar-based backtest cannot know, said out loud
 *
 * When a bar's range contains both the stop and the target, the outcome depends
 * on the path price took inside that bar — and a bar does not record its path.
 * Every backtesting tool faces this; most resolve it silently.
 *
 * This one resolves it pessimistically (stop first) and **counts how often it
 * had to**. `ambiguousBars` travels with the result, so a strategy whose
 * profitability rests on fifty coin-flips resolved in its favour is visibly
 * that, rather than a clean equity curve. A high count is not a bug in the
 * engine; it is a fact about the strategy, and the trader should see it.
 *
 * ## Costs are modelled, not omitted
 *
 * Commission and slippage are subtracted per fill. A backtest without costs is
 * a claim about a market that does not exist, and scalping strategies are
 * exactly the ones it flatters most. Slippage is expressed in TICKS and
 * converted through the instrument master — never a hard-coded dollar figure.
 */
import { dec, ZERO, type Decimal } from '../money.js';
import { getInstrument } from '../instruments/index.js';
import { reconstructTrades } from '../journal/reconstruct.js';
import { summarise, equityCurve, maxDrawdown, type Summary } from '../journal/analytics.js';
import { atr as atrSeries, ema, percentileOfLast, sma } from '../signals/layer1.js';
import { computeLayer1 } from '../signals/layer1.js';
import type { Bar, Fill, Trade } from '../journal/types.js';
import type { MarketRegime } from '../signals/types.js';
import {
  evaluate, seriesNeeded, type HourOf, type SeriesCache, type StrategyRules,
} from './rules.js';

export interface BacktestCosts {
  /** Per contract, per side. From the trader's own broker, not a guess. */
  readonly commissionPerContract: Decimal;
  /** Per contract, per side. */
  readonly feesPerContract: Decimal;
  /**
   * Ticks of slippage per side, converted through the instrument's tick value.
   *
   * Ticks rather than dollars because the honest number differs per contract:
   * one tick is $12.50 on ES and $1.25 on MES, and a dollar figure would model
   * one of them wrongly.
   */
  readonly slippageTicks: number;
}

export const DEFAULT_COSTS: BacktestCosts = {
  commissionPerContract: dec('2.50'),
  feesPerContract: dec('1.50'),
  slippageTicks: 1,
};

export interface BacktestOptions {
  readonly symbol: string;
  readonly timeframe: string;
  readonly bars: readonly Bar[];
  readonly rules: StrategyRules;
  readonly costs?: BacktestCosts;
  /** Injected so `packages/core` keeps no timezone I/O. */
  readonly hourOf?: HourOf;
  /** Account balance the equity curve starts from. */
  readonly startingBalance?: Decimal;
}

export interface BacktestResult {
  readonly symbol: string;
  readonly timeframe: string;
  readonly barCount: number;
  readonly from: Date | null;
  readonly to: Date | null;

  /** Closed round turns. What the summary is computed from. */
  readonly trades: readonly Trade[];
  /**
   * A position still open when the data ran out.
   *
   * Returned rather than dropped. The engine does not mark it out at the last
   * price — that books an unrealised number as a result — but omitting it
   * entirely is worse: a strategy that enters and never exits would report zero
   * trades and look like it simply never fired.
   */
  readonly openTrades: readonly Trade[];
  readonly summary: Summary;
  readonly maxDrawdown: Decimal;
  readonly equityCurve: readonly { readonly at: Date; readonly equity: Decimal; readonly drawdown: Decimal }[];

  /** Total commission and fees the backtest charged itself. */
  readonly costsPaid: Decimal;
  /** Slippage, separately, because it is the cost people forget. */
  readonly slippagePaid: Decimal;

  /**
   * Bars where the stop and the target were both inside the range.
   *
   * Resolved as losses. This number is the honest measure of how much of the
   * result is assumption rather than data.
   */
  readonly ambiguousBars: number;
  /** A trade that was still open when the data ran out. Not counted as a win. */
  readonly openAtEnd: boolean;
  /** Bars skipped because a series was not defined yet. */
  readonly warmupBars: number;
}

/** Build every series a rule tree needs, once. */
function buildCache(
  bars: readonly Bar[], rules: StrategyRules, hourOf: HourOf, symbol: string, timeframe: string,
): SeriesCache {
  const need = seriesNeeded(rules);
  const close = bars.map((b) => b.close);

  const smaMap = new Map<number, readonly number[]>();
  for (const n of need.sma) smaMap.set(n, sma(close, n));
  const emaMap = new Map<number, readonly number[]>();
  for (const n of need.ema) emaMap.set(n, ema(close, n));
  const atrMap = new Map<number, readonly number[]>();
  for (const n of need.atr) atrMap.set(n, atrSeries(bars, n));

  const atr14 = atrMap.get(14) ?? atrSeries(bars, 14);

  // The regime classifier is Layer 1's, run on a trailing window so a rule
  // reading "only in a trend" sees what the chart would have said at the time,
  // not what it says with the whole series in hand.
  //
  // Two things keep it affordable, and both are visible in a profile:
  //
  //  1. **Skipped entirely when unreferenced.** It is roughly 95% of a
  //     backtest's runtime and most rule trees never ask about the regime.
  //  2. **Computed on a stride, held between.** A regime is a slow-moving
  //     property of the market; recomputing it every bar on a five-minute chart
  //     is spurious precision, not accuracy. The stride is in BARS rather than
  //     minutes so it means the same thing at every timeframe.
  const regime: MarketRegime[] = [];
  if (need.regime) {
    const WINDOW = 200;
    const STRIDE = 10;
    let held: MarketRegime = 'unclear';
    for (let i = 0; i < bars.length; i += 1) {
      if (i < 30) { regime.push('unclear'); continue; }
      if ((i - 30) % STRIDE === 0) {
        held = computeLayer1(bars.slice(Math.max(0, i - WINDOW + 1), i + 1),
          { symbol, timeframe }).regime;
      }
      regime.push(held);
    }
  } else {
    // Not 'unclear' repeated — the array is never read, and filling it would be
    // allocating a megabyte to answer a question nobody asked.
    regime.length = 0;
  }

  // Incremental rather than `slice(0, i).filter(...)` per bar, which is
  // quadratic and was the second-largest cost after the regime.
  const atrPercentile: number[] = [];
  const seen: number[] = [];
  for (let i = 0; i < bars.length; i += 1) {
    const v = atr14[i];
    if (v !== undefined && Number.isFinite(v)) seen.push(v);
    atrPercentile.push(seen.length < 20 ? Number.NaN : percentileOfLast(seen, 100));
  }

  return {
    close,
    open: bars.map((b) => b.open),
    high: bars.map((b) => b.high),
    low: bars.map((b) => b.low),
    sma: smaMap, ema: emaMap, atr: atrMap,
    atrPercentile,
    regime,
    hour: bars.map(hourOf),
  };
}

interface OpenPosition {
  readonly direction: 'long' | 'short';
  readonly entryIndex: number;
  readonly entryPrice: number;
  readonly stop: number | null;
  readonly target: number | null;
}

const utcHour = (b: Bar): number => new Date(b.time * 1000).getUTCHours();

export function backtest(opts: BacktestOptions): BacktestResult {
  const { bars, rules, symbol, timeframe } = opts;
  const costs = opts.costs ?? DEFAULT_COSTS;
  const inst = getInstrument(symbol);
  const tickSize = inst.tickSize.toNumber();
  const slip = costs.slippageTicks * tickSize;

  const cache = buildCache(bars, rules, opts.hourOf ?? utcHour, symbol, timeframe);
  const atr14 = cache.atr.get(14) ?? atrSeries(bars, 14);

  const fills: Fill[] = [];
  let position: OpenPosition | null = null;
  let ambiguousBars = 0;
  let warmupBars = 0;
  let slippagePaid = ZERO;
  let costsPaid = ZERO;
  let seq = 0;

  const qty = dec(rules.quantity);
  const perFillCommission = costs.commissionPerContract.times(rules.quantity);
  const perFillFees = costs.feesPerContract.times(rules.quantity);

  /**
   * Slippage is applied to the PRICE, not deducted as a fee.
   *
   * That is what it actually is: a fill worse than the level by some ticks. Put
   * through the price it also flows into the R-multiple and the MAE/MFE the
   * journal computes, which a separate fee line would not.
   */
  const record = (side: 'buy' | 'sell', price: number, at: Date): void => {
    const worse = side === 'buy' ? price + slip : price - slip;
    seq += 1;
    fills.push({
      brokerFillId: `bt-${seq}`,
      accountId: 'backtest',
      symbol,
      side,
      quantity: qty,
      price: dec(worse.toFixed(inst.displayPrecision + 2)),
      commission: perFillCommission,
      fees: perFillFees,
      executedAt: at,
    });
    costsPaid = costsPaid.plus(perFillCommission).plus(perFillFees);
    slippagePaid = slippagePaid.plus(inst.pointValue.times(slip).times(rules.quantity));
  };

  const timeOf = (i: number): Date => new Date(bars[i]!.time * 1000);

  for (let i = 0; i < bars.length; i += 1) {
    const bar = bars[i]!;
    // Narrowed to a number here so the stop and target arithmetic below cannot
    // silently produce NaN prices on a warmup bar.
    const a = atr14[i];
    if (a === undefined || !Number.isFinite(a)) { warmupBars += 1; continue; }

    // --- exits first ------------------------------------------------------
    // Checked before entries so a bar cannot both close and open a position at
    // prices taken from the same candle in whichever order flattered the result.
    if (position) {
      const held = i - position.entryIndex;
      const long = position.direction === 'long';
      const hitStop = position.stop !== null
        && (long ? bar.low <= position.stop : bar.high >= position.stop);
      const hitTarget = position.target !== null
        && (long ? bar.high >= position.target : bar.low <= position.target);

      let exitPrice: number | null = null;

      if (hitStop && hitTarget) {
        // Both inside one bar. The bar does not record which came first, so the
        // engine takes the loss and counts the assumption.
        ambiguousBars += 1;
        exitPrice = position.stop;
      } else if (hitStop) {
        exitPrice = position.stop;
      } else if (hitTarget) {
        exitPrice = position.target;
      } else if (rules.exit.timeStopBars !== undefined && held >= rules.exit.timeStopBars) {
        exitPrice = bar.close;
      } else if (rules.exit.exitOnOpposite) {
        const opposite = long
          ? (rules.shortEntry ? evaluate(rules.shortEntry, i, cache) : false)
          : evaluate(rules.entry, i, cache);
        if (opposite) exitPrice = bar.close;
      }

      if (exitPrice !== null) {
        record(long ? 'sell' : 'buy', exitPrice, timeOf(i));
        position = null;
      }
    }

    // --- entries ----------------------------------------------------------
    if (!position) {
      const wantsLong = rules.direction !== 'short' && evaluate(rules.entry, i, cache);
      const wantsShort = rules.direction !== 'long' && rules.shortEntry !== undefined
        && evaluate(rules.shortEntry, i, cache);

      // Both firing on one bar is a contradiction in the rules, not a signal.
      // Taking neither is the only answer that does not invent a preference.
      if (wantsLong !== wantsShort) {
        const long = wantsLong;
        // Entry at the close of the signalling bar. Not the open of the next:
        // that models a delay the rules did not ask for. Not the high or low:
        // that models foresight.
        const entryPrice = bar.close;
        const stop = rules.exit.stopAtr !== undefined
          ? (long ? entryPrice - a * rules.exit.stopAtr : entryPrice + a * rules.exit.stopAtr)
          : null;
        const target = rules.exit.targetAtr !== undefined
          ? (long ? entryPrice + a * rules.exit.targetAtr : entryPrice - a * rules.exit.targetAtr)
          : null;

        record(long ? 'buy' : 'sell', entryPrice, timeOf(i));
        position = { direction: long ? 'long' : 'short', entryIndex: i, entryPrice, stop, target };
      }
    }
  }

  // A position still open when the data ends is NOT closed at the last price.
  // Marking it out would book an unrealised number as a result. It is returned
  // as an open trade instead, so "entered and never exited" is visible rather
  // than indistinguishable from "never fired".
  const openAtEnd = position !== null;

  const { trades, openTrades } = reconstructTrades(fills);
  const start = opts.startingBalance ?? ZERO;

  return {
    symbol, timeframe,
    barCount: bars.length,
    from: bars.length ? timeOf(0) : null,
    to: bars.length ? timeOf(bars.length - 1) : null,
    trades,
    openTrades,
    summary: summarise(trades),
    maxDrawdown: maxDrawdown(trades, start),
    equityCurve: equityCurve(trades, start),
    costsPaid,
    slippagePaid,
    ambiguousBars,
    openAtEnd,
    warmupBars,
  };
}
